/** Days after the end date during which an expired subscription keeps read-only workspace access. */
export const EXPIRED_GRACE_DAYS = 30

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Where a subscription is relative to its end date:
 * - `active`: not expired (or no / unparseable end date)
 * - `expired`: past the end date, within the grace period — files stay available, the AI chat is
 *   read-only, and only renewal is offered
 * - `lapsed`: expired more than {@link EXPIRED_GRACE_DAYS} days ago — files and the AI chat are off
 */
export type SubscriptionLifecycle = 'active' | 'expired' | 'lapsed'

/** Last day of the grace period (end date + {@link EXPIRED_GRACE_DAYS}); `null` without a valid end date. */
export function getGracePeriodEnd(endDate: string | undefined): Date | null {
  const end = endDate ? new Date(endDate).getTime() : NaN
  return Number.isNaN(end) ? null : new Date(end + EXPIRED_GRACE_DAYS * DAY_MS)
}

/** Expired subscriptions (in or past the grace period) offer only renewal, not add-ons. */
export const isSubscriptionExpired = (lifecycle: SubscriptionLifecycle | null): boolean =>
  lifecycle === 'expired' || lifecycle === 'lapsed'

export function getSubscriptionLifecycle(
  endDate: string | undefined,
  now: number = Date.now()
): SubscriptionLifecycle {
  const end = endDate ? new Date(endDate).getTime() : NaN
  if (Number.isNaN(end) || end >= now) {
    return 'active'
  }
  return now - end > EXPIRED_GRACE_DAYS * DAY_MS ? 'lapsed' : 'expired'
}
